import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { exhaustMap, filter, finalize, from, pipe, tap } from 'rxjs';

import { Account } from '@accounts/models/account';
import { AccountPermissions } from '@accounts/models/account-permissions';
import { AccountsService } from '@accounts/services/accounts.service';
import { AuthService } from '@auth/services/auth.service';
import { USER_STORE } from '@core/stores/user.store';
import { MyProfileService } from '@my-profile/services/my-profile.service';
import { myProfileErrorMessage } from '@my-profile/stores/my-profile.store-helper';
import { ToastNotificationsService } from '@shared/services/toast-notifications.service';

interface MyProfileState {
  accountPermissions: {
    data: AccountPermissions | null;
    loading: boolean;
    loaded: boolean;
  };

  saving: boolean;
  changingPassword: boolean;
  error: HttpErrorResponse | null;

  requestingDeletionIds: Set<string>;
  cancelingDeletionRequestIds: Set<string>;
}

const INITIAL_STATE: MyProfileState = {
  accountPermissions: {
    data: null,
    loading: false,
    loaded: false
  },

  saving: false,
  changingPassword: false,
  error: null,

  requestingDeletionIds: new Set<string>(),
  cancelingDeletionRequestIds: new Set<string>()
};

export const MY_PROFILE_STORE = signalStore(
  withState<MyProfileState>(INITIAL_STATE),

  withMethods(
    (
      store,
      myProfileService = inject(MyProfileService),
      accountsService = inject(AccountsService),
      authService = inject(AuthService),
      toastNotificationsService = inject(ToastNotificationsService),
      userStore = inject(USER_STORE)
    ) => ({
      loadAccountPermissions: rxMethod<void>(
        pipe(
          tap(() =>
            patchState(store, {
              accountPermissions: {
                ...store.accountPermissions(),
                loading: true,
                loaded: false
              },
              error: null
            })
          ),

          exhaustMap(() =>
            myProfileService.getAccountPermissions().pipe(
              tapResponse({
                next: (result: AccountPermissions) => {
                  patchState(store, {
                    accountPermissions: {
                      data: result,
                      loading: false,
                      loaded: true
                    }
                  });
                },

                error: (error: HttpErrorResponse) => {
                  toastNotificationsService.error(
                    myProfileErrorMessage(
                      error,
                      'Loading project permissions failed'
                    )
                  );

                  patchState(store, {
                    accountPermissions: {
                      ...store.accountPermissions(),
                      loading: false,
                      loaded: true
                    },
                    error
                  });
                }
              })
            )
          )
        )
      ),

      update: rxMethod<Account>(
        pipe(
          tap(() => patchState(store, { saving: true, error: null })),

          exhaustMap(account =>
            myProfileService.update(account).pipe(
              tapResponse({
                next: () => {
                  toastNotificationsService.success('Account updated');

                  patchState(store, { saving: false });

                  userStore.updateAccount(account);
                },

                error: (error: HttpErrorResponse) => {
                  toastNotificationsService.error(
                    myProfileErrorMessage(error, 'Update account failed')
                  );

                  patchState(store, {
                    error,
                    saving: false
                  });
                }
              })
            )
          )
        )
      ),

      requestDeletion: rxMethod<string>(
        pipe(
          filter(accountId => !store.requestingDeletionIds().has(accountId)),

          tap(accountId =>
            patchState(store, {
              requestingDeletionIds: new Set([
                ...store.requestingDeletionIds(),
                accountId
              ]),
              error: null
            })
          ),

          exhaustMap(accountId =>
            accountsService.requestDeletion().pipe(
              tapResponse({
                next: () => {
                  toastNotificationsService.success('Account deletion request');

                  userStore.markAccountDeletionRequested(accountId);
                },

                error: (error: HttpErrorResponse) => {
                  toastNotificationsService.error(
                    myProfileErrorMessage(
                      error,
                      'Account delete request failed'
                    )
                  );

                  patchState(store, { error });
                }
              }),
              finalize(() => {
                patchState(store, {
                  requestingDeletionIds: new Set(
                    [...store.requestingDeletionIds()].filter(
                      x => x !== accountId
                    )
                  )
                });
              })
            )
          )
        )
      ),

      cancelDeletionRequest: rxMethod<string>(
        pipe(
          filter(
            accountId => !store.cancelingDeletionRequestIds().has(accountId)
          ),

          tap(accountId =>
            patchState(store, {
              cancelingDeletionRequestIds: new Set([
                ...store.cancelingDeletionRequestIds(),
                accountId
              ]),
              error: null
            })
          ),

          exhaustMap(accountId =>
            accountsService.cancelDeletionRequest().pipe(
              tapResponse({
                next: () => {
                  toastNotificationsService.success(
                    'Canceled request to delete account'
                  );

                  userStore.cancelAccountDeletion(accountId);
                },

                error: (error: HttpErrorResponse) => {
                  toastNotificationsService.error(
                    myProfileErrorMessage(
                      error,
                      'Cancel account delete request failed'
                    )
                  );

                  patchState(store, { error });
                }
              }),
              finalize(() => {
                patchState(store, {
                  cancelingDeletionRequestIds: new Set(
                    [...store.cancelingDeletionRequestIds()].filter(
                      id => id !== accountId
                    )
                  )
                });
              })
            )
          )
        )
      ),

      changePassword: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { changingPassword: true, error: null })),

          exhaustMap(email =>
            from(authService.changePassword(email)).pipe(
              tapResponse({
                next: () => {
                  patchState(store, { changingPassword: false });
                },

                error: (error: HttpErrorResponse) => {
                  toastNotificationsService.error('Change password failed');

                  patchState(store, {
                    changingPassword: false,
                    error
                  });
                }
              })
            )
          )
        )
      ),

      clearMyProfile(): void {
        patchState(store, { ...INITIAL_STATE });
      }
    })
  )
);
